import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';

function renderText(text) {
  if (!text) return null;
  return text.split('\n\n').filter(p => p.trim()).map((p, i) => {
    const trimmed = p.trim();
    // Heading detection: short line without trailing punctuation
    if (trimmed.length < 55 && !trimmed.endsWith('.') && !trimmed.endsWith(',') && !trimmed.includes('\n')) {
      return <h3 key={i} className="reading-heading">{trimmed}</h3>;
    }
    return <p key={i}>{trimmed}</p>;
  });
}

export default function UniversalYearPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/universal-year`)
      .then(res => setData(res.data))
      .catch(() => setError("We couldn't load this year's Universal Year reading. Please try again in a moment."))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '56px 24px 96px' }} data-testid="universal-year-page">
      {/* Hero */}
      <div style={{ textAlign: 'center', marginBottom: 48 }}>
        <div style={{ fontSize: 11, color: 'var(--muted)', letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 14 }}>
          {data ? `${data.year} is a Universal Year` : 'Loading…'}
        </div>

        {data && (
          <div
            style={{
              margin: '0 auto 24px',
              width: 160, height: 160, borderRadius: '50%',
              background: 'linear-gradient(135deg, #15192b 0%, #1d2540 55%, #2d2238 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', position: 'relative', overflow: 'hidden',
              boxShadow: '0 14px 40px rgba(21,25,43,0.25)',
            }}
            data-testid="uy-number-orb"
          >
            <span
              style={{
                position: 'absolute', inset: 0, pointerEvents: 'none',
                backgroundImage:
                  'radial-gradient(circle at 22% 28%, rgba(255,255,255,0.5) 0.8px, transparent 1.6px),' +
                  'radial-gradient(circle at 72% 18%, rgba(255,255,255,0.3) 0.6px, transparent 1.2px),' +
                  'radial-gradient(circle at 34% 72%, rgba(255,255,255,0.35) 0.9px, transparent 1.8px),' +
                  'radial-gradient(circle at 82% 68%, rgba(255,255,255,0.3) 0.7px, transparent 1.4px)',
                opacity: 0.9,
              }}
            />
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 64, fontWeight: 300, lineHeight: 1, position: 'relative' }}>
              {data.number_display}
            </div>
          </div>
        )}

        <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(34px, 5vw, 54px)', fontWeight: 300, color: 'var(--deep)', marginBottom: 14, lineHeight: 1.12 }}>
          {data ? <>Universal Year <em>{data.number_display}</em></> : 'Universal Year'}
        </h1>
        <p style={{ fontSize: 15.5, color: 'var(--body)', lineHeight: 1.75, maxWidth: 520, margin: '0 auto' }}>
          Every year has its own number — a collective energy we all move through together.
          This is what {data?.year || 'this year'} is asking of us, and what gifts it carries.
        </p>
      </div>

      {/* Reading card */}
      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
          <div className="loading-dots"><div className="dot" /><div className="dot" /><div className="dot" /></div>
        </div>
      )}

      {error && (
        <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 16, padding: 32, textAlign: 'center' }} data-testid="uy-error">
          <div style={{ fontSize: 28, marginBottom: 10 }}>✦</div>
          <p style={{ color: 'var(--muted)', marginBottom: 18 }}>{error}</p>
          <button className="tool-btn" onClick={() => window.location.reload()}>Try again</button>
        </div>
      )}

      {!loading && !error && data && (
        <>
          <div className="reading-card" data-testid="uy-reading-card">
            {renderText(data.reading)}
          </div>

          {/* CTA: get your personal reading to layer on top */}
          <div style={{ textAlign: 'center', marginTop: 48, padding: '32px 24px', background: 'linear-gradient(135deg, var(--blue-pale), var(--mauve-pale))', borderRadius: 20 }}>
            <div style={{ fontSize: 30, marginBottom: 12 }}>✦</div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 300, marginBottom: 10 }}>
              Want to know what this year holds <em>for you?</em>
            </div>
            <p style={{ fontSize: 14.5, color: 'var(--body)', lineHeight: 1.75, maxWidth: 440, margin: '0 auto 22px' }}>
              Your Personal Year number sits on top of the Universal Year — it's how this year's collective energy meets your unique blueprint.
            </p>
            <button className="tool-btn" onClick={() => navigate('/tool')} data-testid="uy-cta-tool">
              Get your personal reading →
            </button>
          </div>
        </>
      )}
    </div>
  );
}
